import React, { useEffect, useMemo, useState } from "react";
import { setPostLoginRedirect } from "utils/redirects";
import { AnimatePresence, motion } from "framer-motion";
import ComputeSelector from "./ComputeSelector/index";
import ContainerSelector from "./ContainerSelector/index";
import "./style.scss";
import { validateName } from "components/utils";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";

import FilterBar from "./ComputeSelector/Advanced/FilterBar/index";
import { ContainerSelected } from "../shared/BuildTypes";
import { GPUInstanceType } from "types/InstanceTypes";
import { allPublicCloudsName } from "./utils/index";
import { Button } from "components/Graphs/Button";
import agent from "server";
import { useQuery } from "@tanstack/react-query";
import UnauthenticatedDash from "components/DashboardContainer/UnauthenticatedDash";
import { useHistory } from "react-router";
import { useKas } from "contexts/KasContext";
import { redirectUserToLogin } from "server/kas/utils";
import { createGpuCpuEnvironmentPlaceholderPair } from "utils/defaultEnvironmentDisplayName";
import { getGpuQueryValueForInstance } from "./UnifiedCreate/createEnvironmentUrlState";

export const filterForGPUInstances = (
  instances: GPUInstanceType[],
  includeSFCompute: boolean = true,
) => {
  return instances
    .filter(
      (gpu) =>
        gpu.supported_gpus &&
        gpu.supported_gpus.length > 0 &&
        gpu.supported_gpus[0],
    )
    .filter((gpu) => gpu.supported_gpus[0].manufacturer !== "Habana")
    .filter((gpu) => gpu.supported_gpus[0].manufacturer !== "AMD")
    .filter((gpu) => (includeSFCompute ? true : gpu.provider !== "sfcompute"))
    .map((gpu) => ({
      ...gpu,
      supported_gpus: [
        {
          ...gpu.supported_gpus[0],
          name: formatGPUName(gpu.supported_gpus[0].name),
        },
      ] as [(typeof gpu.supported_gpus)[0]],
    }));
};

export const formatGPUName = (name: string): string => {
  let formattedName = name;

  // Only process if the name contains RTX, Pro/PRO, or Ada
  if (
    formattedName.includes("RTX") ||
    formattedName.toUpperCase().includes("PRO") ||
    formattedName.includes("Ada")
  ) {
    // Add space after RTX if not already spaced
    formattedName = formattedName.replace(/RTX(?=[A-Za-z0-9])/g, "RTX ");

    // Add spaces around Pro/PRO if not already spaced and normalize to PRO
    formattedName = formattedName.replace(
      /([A-Za-z])(Pro|PRO)(?=[A-Za-z0-9])/g,
      "$1 PRO ",
    );
    formattedName = formattedName.replace(/(Pro|PRO)([A-Za-z0-9])/g, "PRO $2");
    // Normalize Pro/PRO at end of string or before whitespace
    formattedName = formattedName.replace(/(Pro|PRO)(?=\s|$)/g, "PRO");

    // Add spaces around Ada if not already spaced
    formattedName = formattedName.replace(
      /([A-Za-z0-9])Ada(?=[A-Za-z0-9])/g,
      "$1 Ada ",
    );
    formattedName = formattedName.replace(/([A-Za-z0-9])Ada$/g, "$1 Ada");
    formattedName = formattedName.replace(/Ada([A-Za-z0-9])/g, "Ada $1");

    // Add space between letters and numbers for these special cases only
    formattedName = formattedName.replace(/([A-Za-z])([0-9])/g, "$1 $2");
  }

  // Clean up multiple consecutive spaces
  formattedName = formattedName.replace(/\s+/g, " ").trim();

  return formattedName;
};

const EnvironmentCreatePublic: React.FC = () => {
  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);
  const instanceFromQuery = queryParams.get("instanceType");

  const [container, setContainer] = useState<ContainerSelected>({
    vmBuild: {
      forceJupyterInstall: false,
    },
  });
  const [pythonVersion, setPythonVersion] = useState<string>("3.10");
  const [cudaVersion, setCudaVersion] = useState<string>("12.0.1");

  const [environmentNamePlaceholders] = useState(
    createGpuCpuEnvironmentPlaceholderPair,
  );
  const [environmentName, setEnvironmentName] = useState(
    () => environmentNamePlaceholders.gpu,
  );
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");
  const [selectedInstanceType, setSelectedInstanceType] =
    useState<GPUInstanceType>();
  const [storage, setStorage] = useState<string>("256");
  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);
  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [displayCPUInstances, setDisplayCPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [persistentOnly, setPersistentOnly] = useState<boolean>(false);
  const [flexiblePortControlOnly, setFlexiblePortControlOnly] =
    useState<boolean>(false);
  const [flexibleStorageOnly, setFlexibleStorageOnly] =
    useState<boolean>(false);

  useEffect(() => {
    if (selectedInstanceType) {
      const isCPU =
        !selectedInstanceType.supported_gpus ||
        selectedInstanceType.supported_gpus.length === 0;
      const newDefault = isCPU
        ? environmentNamePlaceholders.cpu
        : environmentNamePlaceholders.gpu;
      setEnvironmentName((prev) =>
        prev === environmentNamePlaceholders.gpu ||
        prev === environmentNamePlaceholders.cpu
          ? newDefault
          : prev,
      );
    }
  }, [selectedInstanceType, environmentNamePlaceholders]);

  const { isUserLoggedIn, user, isLoading: isAuthLoading } = useKas();

  const getAllPublicInstanceTypes = async () => {
    const res = await agent.Instances.getAllPublicInstanceTypes();
    if (!res.success || !res.data) {
      throw new Error("Failed to fetch workspaces");
    }
    return res.data;
  };

  const { data: instancesWithErrors, isLoading: isInstancesLoading } = useQuery(
    {
      queryKey: ["instances-with-errors"],
      queryFn: () => getAllPublicInstanceTypes(),
    },
  );

  const cpuInstances = useMemo(() => {
    if (!instancesWithErrors) return [];
    return instancesWithErrors.allInstanceTypes.filter(
      (instance) =>
        !instance.supported_gpus || instance.supported_gpus.length === 0,
    );
  }, [instancesWithErrors]);

  const allGPUInstances = useMemo(() => {
    if (!instancesWithErrors) return [];
    return filterForGPUInstances(instancesWithErrors.allInstanceTypes).filter(
      (instance) =>
        !instance.supported_gpus[0].name.includes("5090") &&
        !instance.supported_gpus[0].name.includes("4090"),
    );
  }, [instancesWithErrors]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    if (isUserLoggedIn && user) {
      history.push(`/environment/new?${queryParams.toString()}`);
    }
  }, [isUserLoggedIn, user, isAuthLoading, queryParams]);

  const handleNameChange = (val: string) => {
    setEnvironmentName(val);

    // Set validation error messaging
    if (val === "") {
      setNameFieldErrorMessage("Name required!");
      return;
    }
    const isValid = validateName(val);
    if (!isValid) {
      setNameFieldErrorMessage(
        "Name can only have letters, numbers and dashes.",
      );
      return;
    }
    setNameFieldErrorMessage("");
  };

  useEffect(() => {
    if (instanceFromQuery) {
      const found = allGPUInstances.find((i) => i.type === instanceFromQuery);
      if (found) {
        setSelectedInstanceType(found);
      } else {
        console.error("Instance type not found");
      }
    }
  }, [instanceFromQuery, allGPUInstances]);

  return (
    <UnauthenticatedDash>
      <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-10 pt-3">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between mt-8">
            <div className="flex mb-2">
              <h1 className="text-xl  sm:text-2xl font-bold text-gray-900 dark:text-slate-100">
                {!container && "Select your Container"}
                {container && !selectedInstanceType && "Select your Compute"}
                {container && !!selectedInstanceType && "Configure and Deploy"}
              </h1>
            </div>
            <div className="flex-row items-end hidden sm:flex">
              {container && !selectedInstanceType && (
                <FilterBar
                  allGPUInstances={allGPUInstances}
                  allCPUInstances={cpuInstances}
                  setDisplayGPUInstances={setDisplayGPUInstances}
                  setDisplayCPUInstances={setDisplayCPUInstances}
                  containerSelected={container}
                  onFilterStateChange={(
                    persistent,
                    flexiblePorts,
                    flexibleStorage,
                  ) => {
                    setPersistentOnly(persistent);
                    setFlexiblePortControlOnly(flexiblePorts);
                    setFlexibleStorageOnly(flexibleStorage);
                  }}
                />
              )}
            </div>
          </div>
          <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
          <div className="flex flex-col">
            <AnimatePresence initial={false}>
              {!container && (
                <motion.section
                  key="container"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { opacity: 1, height: "auto" },
                    collapsed: { opacity: 0, height: 0 },
                  }}
                  transition={{
                    duration: 0.8,
                    ease: [0.04, 0.62, 0.23, 0.98],
                  }}
                >
                  <motion.div
                    variants={{
                      collapsed: { scale: 0.8 },
                      open: { scale: 1 },
                    }}
                    transition={{ duration: 0.8 }}
                    className="content-placeholder"
                  >
                    <ContainerSelector
                      hideTitle={true}
                      containerSelected={container}
                      onSelectContainer={setContainer}
                      pythonVersion={pythonVersion}
                      setPythonVersion={setPythonVersion}
                      cudaVersion={cudaVersion}
                      setCudaVersion={setCudaVersion}
                    />
                  </motion.div>
                </motion.section>
              )}
              {container && (
                <>
                  <ComputeSelector
                    allGPUInstances={allGPUInstances}
                    isLoadingInstances={isInstancesLoading}
                    displayGPUInstances={displayGPUInstances}
                    selectedInstance={selectedInstanceType}
                    setSelectedInstance={setSelectedInstanceType}
                    setStorage={setStorage}
                    storage={storage}
                    containerSelected={container}
                    selectedCloudName={selectedCloudName}
                    persistentOnly={persistentOnly}
                    flexiblePortControlOnly={flexiblePortControlOnly}
                    flexibleStorageOnly={flexibleStorageOnly}
                    cpuInstances={displayCPUInstances ?? cpuInstances}
                  />
                  {!!selectedInstanceType && (
                    <div className="flex flex-col mt-10">
                      <div>
                        <Button
                          onClick={() => {
                            const redirectParams = new URLSearchParams(
                              window.location.search,
                            );
                            redirectParams.set(
                              "instanceType",
                              selectedInstanceType.type,
                            );
                            redirectParams.set(
                              "gpu",
                              getGpuQueryValueForInstance(selectedInstanceType),
                            );
                            if (
                              selectedInstanceType.elastic_root_volume &&
                              storage
                            ) {
                              redirectParams.set("storage", storage);
                            } else {
                              redirectParams.delete("storage");
                            }
                            setPostLoginRedirect(
                              `/environment/new?${redirectParams.toString()}`,
                            );
                            redirectUserToLogin();
                          }}
                        >
                          <span>Deploy</span>
                          <ArrowUpRightIcon className="ml-1 w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </UnauthenticatedDash>
  );
};

export default EnvironmentCreatePublic;
